import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MapPin, Calendar, LogOut, CalendarDays, Users, AlertTriangle, Bell, CreditCard, Camera } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "../components/ui/sonner";
import { DashboardStatCards } from "../components/DashboardStatCards";
import { DashboardActionItems } from "../components/DashboardActionItems";
import { DashboardUpcomingTable } from "../components/DashboardUpcomingTable";
import { formatDate } from "../lib/dateUtils";
import { mediaUrl } from "../lib/mediaUrl";
import { SortablePortfolioGrid } from "../components/SortablePortfolioGrid";

const EMPTY_VERIFY = { legal_name: "", business_name: "", business_info: "", website: "", instagram: "", portfolio_note: "", service_location: "", phone: "", address: "", agreement_accepted: false, safety_standards_accepted: false };

const EMPTY_BUSINESS_PROFILE = {
  business_name: "", bio: "", location: "", service_area: "", website: "", instagram: "", facebook: "", tiktok: "", years_experience: "", specialties: "",
};

const EMPTY_PAYMENT_SETTINGS = {
  session_payment_required: true, payment_timing: "pay_in_person", accepted_payment_methods: ["venmo", "cashapp"],
  venmo_username: "", cashapp_username: "", pay_in_person_enabled: true, other_payment_instructions: "", deposit_info: "", cancellation_info: "",
};

const PAYMENT_METHOD_OPTIONS = [
  { value: "venmo", label: "Venmo" },
  { value: "cashapp", label: "Cash App" },
  { value: "in_person", label: "In Person (cash/card)" },
  { value: "other", label: "Other" },
];

export default function PhotographerDashboard() {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [verifyForm, setVerifyForm] = useState(EMPTY_VERIFY);
  const [submitting, setSubmitting] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState(EMPTY_PAYMENT_SETTINGS);
  const [savingPayment, setSavingPayment] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [businessForm, setBusinessForm] = useState(EMPTY_BUSINESS_PROFILE);
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);

  const load = () => {
    api.get("/photographers/me").then(({ data }) => {
      setProfile(data);
      setBusinessForm({
        business_name: data.business_name || "", bio: data.bio || "", location: data.location || "",
        service_area: data.service_area || "", website: data.website || "", instagram: data.instagram || "",
        facebook: data.facebook || "", tiktok: data.tiktok || "", years_experience: data.years_experience ?? "",
        specialties: (data.specialties || []).join(", "),
      });
      if (data.payment_settings) setPaymentSettings({ ...EMPTY_PAYMENT_SETTINGS, ...data.payment_settings });
    });
    api.get("/sessions/mine").then(({ data }) => setSessions(data));
    api.get("/photographers/me/dashboard").then(({ data }) => setDashboard(data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const statCards = dashboard
    ? [
        { key: "total_sessions", label: "Total Sessions", value: dashboard.stats.total_sessions, icon: CalendarDays },
        { key: "upcoming_sessions", label: "Upcoming", value: dashboard.stats.upcoming_sessions, icon: Calendar },
        { key: "total_registrations", label: "Registrations", value: dashboard.stats.total_registrations, icon: Users },
        { key: "unmatched_registrations", label: "Unmatched", value: dashboard.stats.unmatched_registrations, icon: AlertTriangle },
        { key: "unread_notifications", label: "Unread", value: dashboard.stats.unread_notifications, icon: Bell },
        { key: "pending_own_fees", label: "Fees Due", value: dashboard.stats.pending_own_fees, icon: CreditCard },
      ]
    : [];

  const toggleMethod = (value) => {
    setPaymentSettings((p) => ({
      ...p,
      accepted_payment_methods: p.accepted_payment_methods.includes(value) ? p.accepted_payment_methods.filter((m) => m !== value) : [...p.accepted_payment_methods, value],
    }));
  };

  const savePaymentSettings = async () => {
    setSavingPayment(true);
    try {
      const { data } = await api.put("/photographers/me/payment-settings", paymentSettings);
      setProfile(data);
      toast.success("Payment settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingPayment(false);
    }
  };

  const saveBusinessProfile = async () => {
    setSavingBusiness(true);
    try {
      const payload = {
        ...businessForm,
        profile_photo_url: profile?.profile_photo_url || null,
        portfolio_urls: profile?.portfolio_urls || [],
        years_experience: businessForm.years_experience === "" ? null : Number(businessForm.years_experience),
        specialties: businessForm.specialties.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.put("/photographers/me", payload);
      setProfile(data);
      toast.success("Business profile saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingBusiness(false);
    }
  };

  const uploadProfilePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/photographers/me/profile-photo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile(data);
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const uploadPortfolio = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPortfolio(true);
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    try {
      const { data } = await api.post("/photographers/me/portfolio", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile(data);
      toast.success("Portfolio updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploadingPortfolio(false);
      e.target.value = "";
    }
  };

  const removePortfolioPhoto = async (path) => {
    try {
      const { data } = await api.delete("/photographers/me/portfolio", { data: { path } });
      setProfile(data);
      toast.success("Photo removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reorderPortfolio = async (newOrder) => {
    setProfile((p) => ({ ...p, portfolio_urls: newOrder }));
    try {
      const { data } = await api.put("/photographers/me/portfolio/reorder", { order: newOrder });
      setProfile(data);
    } catch (err) {
      toast.error("Couldn't save new order");
    }
  };

  const submitVerification = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/photographers/verification", verifyForm);
      setProfile(data);
      toast.success("Verification submitted! We'll review it shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14" data-testid="photographer-dashboard-page">
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="text-stranger-orange text-xs font-bold tracking-[0.25em] uppercase mb-2">Photographer Dashboard</p>
          <h1 className="font-extrabold uppercase text-2xl sm:text-3xl tracking-tight">Hey {user.first_name}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/dashboard/sessions/new" data-testid="create-session-link">
            <button className="flex items-center gap-2 bg-stranger-orange text-clean-white font-bold tracking-widest text-xs px-5 py-3 rounded-full hover:bg-orange-600 transition-colors">
              <Plus className="w-4 h-4" /> CREATE SESSION
            </button>
          </Link>
          <button onClick={logout} data-testid="logout-button" className="flex items-center gap-2 text-xs font-bold tracking-widest px-5 py-3 rounded-full border-2 border-soft-gray hover:border-stranger-orange hover:text-stranger-orange transition-colors">
            <LogOut className="w-3.5 h-3.5" /> LOG OUT
          </button>
        </div>
      </div>

      {profile && (
        <div className="border border-soft-gray rounded-2xl p-6 mb-10" data-testid="verification-status-card">
          {profile.verification_status === "approved" ? (
            <div className="flex items-center gap-3">
              <VerifiedBadge /> <span className="text-sm font-medium text-midnight-black/70">You're an Official Stranger Sessions Photographer.</span>
            </div>
          ) : profile.verification_status === "pending" ? (
            <p className="text-sm font-bold text-midnight-black/80" data-testid="verification-pending">
              Your verification is under review. We'll notify you once it's approved.
            </p>
          ) : profile.verification_status === "suspended" ? (
            <div data-testid="verification-suspended">
              <h2 className="font-extrabold uppercase text-sm mb-2 text-red-600">Hosting Privileges Suspended</h2>
              <p className="text-sm font-medium text-midnight-black/70">{profile.suspended_reason || "Contact support for details."}</p>
            </div>
          ) : (
            <div>
              {profile.verification_status === "needs_info" && (
                <div className="bg-warm-white border border-stranger-orange/40 rounded-xl p-4 mb-5" data-testid="verification-needs-info-banner">
                  <p className="text-xs font-bold uppercase tracking-wide text-stranger-orange mb-1">We need more information</p>
                  <p className="text-sm font-medium text-midnight-black/80">{profile.info_requested_message}</p>
                </div>
              )}
              {profile.verification_status === "rejected" && profile.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5" data-testid="verification-rejected-banner">
                  <p className="text-xs font-bold uppercase tracking-wide text-red-600 mb-1">Application not approved</p>
                  <p className="text-sm font-medium text-midnight-black/80">{profile.rejection_reason}</p>
                </div>
              )}
              <h2 className="font-extrabold uppercase text-sm mb-4">{profile.verification_status === "rejected" || profile.verification_status === "needs_info" ? "Update & Resubmit" : "Get Verified"}</h2>
              <form onSubmit={submitVerification} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="verification-form">
                <Input data-testid="verify-legal-name" placeholder="Legal name" required value={verifyForm.legal_name} onChange={(e) => setVerifyForm({ ...verifyForm, legal_name: e.target.value })} />
                <Input data-testid="verify-business-name" placeholder="Business name" value={verifyForm.business_name} onChange={(e) => setVerifyForm({ ...verifyForm, business_name: e.target.value })} />
                <Input data-testid="verify-phone" placeholder="Phone" value={verifyForm.phone} onChange={(e) => setVerifyForm({ ...verifyForm, phone: e.target.value })} />
                <Input data-testid="verify-address" placeholder="Address (kept private)" value={verifyForm.address} onChange={(e) => setVerifyForm({ ...verifyForm, address: e.target.value })} />
                <Input data-testid="verify-website" placeholder="Website" value={verifyForm.website} onChange={(e) => setVerifyForm({ ...verifyForm, website: e.target.value })} />
                <Input data-testid="verify-instagram" placeholder="Instagram handle" value={verifyForm.instagram} onChange={(e) => setVerifyForm({ ...verifyForm, instagram: e.target.value })} />
                <Input data-testid="verify-service-location" placeholder="Service location (city, state)" required value={verifyForm.service_location} onChange={(e) => setVerifyForm({ ...verifyForm, service_location: e.target.value })} />
                <Input data-testid="verify-portfolio-note" placeholder="Portfolio link" value={verifyForm.portfolio_note} onChange={(e) => setVerifyForm({ ...verifyForm, portfolio_note: e.target.value })} />
                <Textarea data-testid="verify-business-info" placeholder="Tell us about your photography business" className="sm:col-span-2" value={verifyForm.business_info} onChange={(e) => setVerifyForm({ ...verifyForm, business_info: e.target.value })} />
                <label className="flex items-center gap-2 sm:col-span-2 text-sm font-medium">
                  <input type="checkbox" data-testid="verify-agreement-checkbox" checked={verifyForm.agreement_accepted} onChange={(e) => setVerifyForm({ ...verifyForm, agreement_accepted: e.target.checked })} required />
                  I agree to the Photographer Agreement and Stranger Sessions conduct expectations.
                </label>
                <label className="flex items-center gap-2 sm:col-span-2 text-sm font-medium">
                  <input type="checkbox" data-testid="verify-safety-checkbox" checked={verifyForm.safety_standards_accepted} onChange={(e) => setVerifyForm({ ...verifyForm, safety_standards_accepted: e.target.checked })} required />
                  I agree to Stranger Sessions' Safety Standards for in-person sessions.
                </label>
                <Button type="submit" data-testid="submit-verification-button" disabled={submitting} className="sm:col-span-2 bg-midnight-black text-clean-white font-bold rounded-full">
                  {submitting ? "Submitting..." : "SUBMIT FOR VERIFICATION"}
                </Button>
              </form>
            </div>
          )}
        </div>
      )}

      {dashboard && <DashboardStatCards cards={statCards} />}

      {dashboard && (
        <div className="mb-14">
          <h2 className="font-extrabold uppercase text-lg mb-4">Needs Your Attention</h2>
          <DashboardActionItems items={dashboard.action_items} emptyMessage="All caught up — nothing needs your attention right now." />
        </div>
      )}

      {dashboard && (
        <div className="mb-14">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-extrabold uppercase text-lg">Upcoming Sessions</h2>
          </div>
          <DashboardUpcomingTable sessions={dashboard.upcoming_sessions} />
        </div>
      )}

      <div className="border border-soft-gray rounded-2xl p-6 mb-14" data-testid="business-profile-card">
        <h2 className="font-extrabold uppercase text-lg mb-1">Business Profile</h2>
        <p className="text-xs font-medium text-midnight-black/50 mb-6">This is what Strangers see on your public photographer profile.</p>

        <div className="flex items-center gap-6 mb-8 flex-wrap">
          <div className="w-24 h-24 rounded-full border-2 border-soft-gray overflow-hidden bg-warm-white flex items-center justify-center shrink-0">
            {profile?.profile_photo_url ? (
              <img src={mediaUrl(profile.profile_photo_url)} alt="Your profile" className="w-full h-full object-cover" data-testid="my-profile-photo-preview" />
            ) : (
              <Camera className="w-8 h-8 text-midnight-black/30" />
            )}
          </div>
          <label className="cursor-pointer">
            <span className="inline-block text-xs font-bold tracking-widest uppercase border-2 border-midnight-black px-6 py-3 rounded-full hover:bg-midnight-black hover:text-clean-white transition-colors">
              {uploadingPhoto ? "Uploading..." : "Upload Profile Photo"}
            </span>
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="my-profile-photo-input" onChange={uploadProfilePhoto} disabled={uploadingPhoto} />
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <Input data-testid="business-name-input" placeholder="Business name" value={businessForm.business_name} onChange={(e) => setBusinessForm({ ...businessForm, business_name: e.target.value })} />
          <Input data-testid="business-location-input" placeholder="Location (city, state)" value={businessForm.location} onChange={(e) => setBusinessForm({ ...businessForm, location: e.target.value })} />
          <Input data-testid="business-service-area-input" placeholder="Service area" value={businessForm.service_area} onChange={(e) => setBusinessForm({ ...businessForm, service_area: e.target.value })} />
          <Input data-testid="business-years-experience-input" type="number" placeholder="Years of experience" value={businessForm.years_experience} onChange={(e) => setBusinessForm({ ...businessForm, years_experience: e.target.value })} />
          <Input data-testid="business-website-input" placeholder="Website" value={businessForm.website} onChange={(e) => setBusinessForm({ ...businessForm, website: e.target.value })} />
          <Input data-testid="business-instagram-input" placeholder="Instagram handle" value={businessForm.instagram} onChange={(e) => setBusinessForm({ ...businessForm, instagram: e.target.value })} />
          <Input data-testid="business-facebook-input" placeholder="Facebook" value={businessForm.facebook} onChange={(e) => setBusinessForm({ ...businessForm, facebook: e.target.value })} />
          <Input data-testid="business-tiktok-input" placeholder="TikTok" value={businessForm.tiktok} onChange={(e) => setBusinessForm({ ...businessForm, tiktok: e.target.value })} />
        </div>
        <Textarea data-testid="business-bio-input" placeholder="Tell Strangers about your photography style" rows={4} className="mb-4" value={businessForm.bio} onChange={(e) => setBusinessForm({ ...businessForm, bio: e.target.value })} />
        <Input data-testid="business-specialties-input" placeholder="Specialties (comma separated, e.g. Golden Hour, Portraits, Outdoor)" className="mb-6" value={businessForm.specialties} onChange={(e) => setBusinessForm({ ...businessForm, specialties: e.target.value })} />

        <Button data-testid="save-business-profile-button" onClick={saveBusinessProfile} disabled={savingBusiness} className="bg-midnight-black hover:bg-black text-clean-white font-bold rounded-full mb-10">
          {savingBusiness ? "Saving..." : "SAVE BUSINESS PROFILE"}
        </Button>

        <div className="border-t border-soft-gray pt-6">
          <h3 className="font-extrabold uppercase text-sm mb-1">Portfolio</h3>
          <p className="text-xs font-medium text-midnight-black/50 mb-4">Sample work shown on your public profile. Upload as many as you'd like.</p>
          {profile?.portfolio_urls?.length > 0 && (
            <SortablePortfolioGrid items={profile.portfolio_urls} onReorder={reorderPortfolio} onRemove={removePortfolioPhoto} />
          )}
          <label className="cursor-pointer">
            <span className="inline-block text-xs font-bold tracking-widest uppercase border-2 border-midnight-black px-6 py-3 rounded-full hover:bg-midnight-black hover:text-clean-white transition-colors">
              {uploadingPortfolio ? "Uploading..." : "Add Portfolio Photos"}
            </span>
            <input type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" data-testid="my-portfolio-input" onChange={uploadPortfolio} disabled={uploadingPortfolio} />
          </label>
        </div>
      </div>

      <div className="border border-soft-gray rounded-2xl p-6 mb-14" data-testid="payment-settings-card">
        <h2 className="font-extrabold uppercase text-lg mb-1">Payment Settings</h2>
        <p className="text-xs font-medium text-midnight-black/50 mb-6">How Strangers pay YOU for the session itself. This is separate from the Stranger Sessions platform booking fee.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">When do you collect payment?</label>
            <Select value={paymentSettings.payment_timing} onValueChange={(v) => setPaymentSettings({ ...paymentSettings, payment_timing: v })}>
              <SelectTrigger data-testid="payment-timing-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pay_in_person">Pay In Person (at session)</SelectItem>
                <SelectItem value="pay_before_session">Pay Before Session</SelectItem>
                <SelectItem value="pay_after_session">Pay After Session</SelectItem>
                <SelectItem value="contact_photographer">Contact Me to Arrange</SelectItem>
                <SelectItem value="custom">Custom (see instructions below)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Accepted Methods</label>
            <div className="flex flex-wrap gap-3 pt-2">
              {PAYMENT_METHOD_OPTIONS.map((m) => (
                <label key={m.value} className="flex items-center gap-1.5 text-xs font-medium">
                  <input type="checkbox" data-testid={`payment-method-${m.value}`} checked={paymentSettings.accepted_payment_methods.includes(m.value)} onChange={() => toggleMethod(m.value)} />
                  {m.label}
                </label>
              ))}
            </div>
          </div>
          <Input data-testid="payment-venmo-username-input" placeholder="Your Venmo username" value={paymentSettings.venmo_username || ""} onChange={(e) => setPaymentSettings({ ...paymentSettings, venmo_username: e.target.value })} />
          <Input data-testid="payment-cashapp-username-input" placeholder="Your Cash App username" value={paymentSettings.cashapp_username || ""} onChange={(e) => setPaymentSettings({ ...paymentSettings, cashapp_username: e.target.value })} />
        </div>
        <Textarea data-testid="payment-other-instructions-input" placeholder="Other payment instructions (optional)" value={paymentSettings.other_payment_instructions || ""} onChange={(e) => setPaymentSettings({ ...paymentSettings, other_payment_instructions: e.target.value })} className="mb-4" />
        <Textarea data-testid="payment-deposit-info-input" placeholder="Deposit info (optional)" value={paymentSettings.deposit_info || ""} onChange={(e) => setPaymentSettings({ ...paymentSettings, deposit_info: e.target.value })} className="mb-4" />
        <Textarea data-testid="payment-cancellation-info-input" placeholder="Your cancellation policy for session payment (optional)" value={paymentSettings.cancellation_info || ""} onChange={(e) => setPaymentSettings({ ...paymentSettings, cancellation_info: e.target.value })} className="mb-6" />
        <Button data-testid="save-payment-settings-button" onClick={savePaymentSettings} disabled={savingPayment} className="bg-midnight-black text-clean-white font-bold rounded-full">
          {savingPayment ? "Saving..." : "SAVE PAYMENT SETTINGS"}
        </Button>
      </div>

      <h2 className="font-extrabold uppercase text-lg mb-6">Your Sessions</h2>
      {sessions === null ? (
        <p className="font-medium text-midnight-black/60" data-testid="sessions-loading">
          Loading your sessions...
        </p>
      ) : sessions.length === 0 ? (
        <div className="border border-soft-gray rounded-2xl p-10 text-center" data-testid="no-sessions">
          <p className="font-medium text-midnight-black/70">You haven't created a session yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="photographer-sessions-grid">
          {sessions.map((s) => (
            <div key={s.id} className="border border-soft-gray rounded-2xl overflow-hidden" data-testid={`photographer-session-${s.id}`}>
              <div className="relative h-36">
                <img src={s.image_url} alt={s.title} className="w-full h-full object-cover" />
                <span className="absolute top-3 left-3 bg-midnight-black/80 text-clean-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">{s.status}</span>
              </div>
              <div className="p-5">
                <h3 className="font-extrabold uppercase text-sm mb-2">{s.title}</h3>
                <p className="text-xs font-medium text-midnight-black/60 flex items-center gap-1.5 mb-1">
                  <MapPin className="w-3.5 h-3.5 text-stranger-orange" /> {s.city}, {s.state}
                </p>
                <p className="text-xs font-medium text-midnight-black/60 flex items-center gap-1.5 mb-4">
                  <Calendar className="w-3.5 h-3.5 text-stranger-orange" /> {formatDate(s.session_date)} · {s.spots_remaining}/{s.capacity} spots
                </p>
                <div className="flex gap-2">
                  <Link to={`/dashboard/sessions/${s.id}/manage`} data-testid={`manage-session-${s.id}`} className="flex-1">
                    <button className="w-full text-[11px] font-bold tracking-widest bg-midnight-black text-clean-white py-2.5 rounded-full hover:bg-stranger-orange transition-colors">MANAGE</button>
                  </Link>
                  <Link to={`/dashboard/sessions/${s.id}/edit`} data-testid={`edit-session-${s.id}`} className="flex-1">
                    <button className="w-full text-[11px] font-bold tracking-widest border-2 border-midnight-black py-2.5 rounded-full hover:bg-midnight-black hover:text-clean-white transition-colors">EDIT</button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
